import {
  Alert,
  Box,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { Link as RouterLink } from "react-router";
import { api } from "../lib/api.js";
import { useContentList, type PublicContent } from "../hooks/useApiQueries.js";
import { ContentCard } from "../components/ContentCard.js";
import { SaveButton } from "../components/SaveButton.js";
import { VideoLightbox } from "../components/VideoLightbox.js";
import { useVideoLightbox } from "../hooks/useVideoLightbox.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow } from "../components/Neon.js";

interface PublicPost {
  id: string;
  text: string;
  mediaUrl: string | null;
  mediaType: string | null;
  platforms: string[];
  platformResults: Array<{ platform: string; url: string }>;
  publishedAt: string;
}

const FILTERS = [
  { value: "all", label: "All" },
  { value: "article", label: "Articles" },
  { value: "facebook", label: "Facebook" },
  { value: "user-gen", label: "User-gen" },
  { value: "news", label: "Industry news" },
] as const;
type FilterValue = (typeof FILTERS)[number]["value"];

export function Read() {
  const [filter, setFilter] = useState<FilterValue>("all");
  const { openFor: openLightbox, lightboxProps } = useVideoLightbox();

  const {
    data: articlesRes,
    isLoading: articlesLoading,
    isError: articlesError,
  } = useContentList({
    type: "ARTICLE",
    pageSize: 24,
  });
  const {
    data: postsRes,
    isLoading: postsLoading,
    isError: postsError,
  } = useQuery({
    queryKey: ["public", "posts", "read"],
    queryFn: () => api<{ data: PublicPost[] }>("/v1/posts?page=1&limit=20"),
  });

  const articles = articlesRes?.data ?? [];
  const posts = postsRes?.data ?? [];

  const showArticles = filter === "all" || filter === "article";
  const showPosts = filter === "all" || filter === "facebook";

  // Surface a genuine load failure instead of a blank page that reads as "no
  // content". Only flag the source(s) the current tab actually depends on.
  const loadFailed = (showArticles && articlesError) || (showPosts && postsError);

  // Per-tab "coming soon" copy. Facebook only shows it when there are genuinely
  // no posts yet (it has a real data source); user-gen and news have none.
  const comingSoonMessage: string | null =
    filter === "facebook" && !postsLoading && posts.length === 0
      ? "Coming soon — Facebook posts from the Corian Park community will land here."
      : filter === "user-gen"
        ? "Your stories coming soon — operators like you sharing what you know."
        : filter === "news"
          ? "Coming soon — we're working on bringing industry news into the feed."
          : null;

  return (
    <>
      <SEO
        title="Read — Australian earthmoving articles + social"
        description="Articles, social posts and stories from Australian earthmoving — curated by the Corian Park team."
        canonical="https://corianpark.au/read"
      />

      <Box sx={{ mb: 4 }}>
        <NeonEyebrow glow>WORDS</NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 38, md: 56 },
            fontWeight: 900,
            mt: 1,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
          }}
        >
          Read
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600 }}>
          Articles, Facebook posts, and the kind of long-form coverage that doesn't fit in a video.
          Endorsed by the Corian Park team.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            onClick={() => setFilter(f.value)}
            color={filter === f.value ? "primary" : "default"}
            variant={filter === f.value ? "filled" : "outlined"}
            sx={{ fontWeight: filter === f.value ? 700 : 400 }}
          />
        ))}
      </Stack>

      {loadFailed && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="read-load-error">
          Couldn't load this content right now. Refresh the page or try again shortly.
        </Alert>
      )}

      {comingSoonMessage && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="read-coming-soon">
          {comingSoonMessage}
        </Alert>
      )}

      {(articlesLoading || postsLoading) && (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rectangular" height={220} />
            </Grid>
          ))}
        </Grid>
      )}

      {showArticles && articles.length > 0 && (
        <Box sx={{ mb: 5 }}>
          {filter === "all" && (
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 2, letterSpacing: 2 }}>
              Articles
            </Typography>
          )}
          <Grid container spacing={2}>
            {articles.map((c: PublicContent) => (
              <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <ContentCard item={c} showSaveButton onVideoClick={openLightbox} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {showPosts && posts.length > 0 && (
        <Box>
          {filter === "all" && (
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 2, letterSpacing: 2 }}>
              From Facebook
            </Typography>
          )}
          <Stack spacing={2}>
            {posts.slice(0, filter === "all" ? 6 : 20).map((p) => (
              <Box
                key={p.id}
                sx={{
                  position: "relative",
                  p: 2.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                  <SaveButton itemType="post" itemId={p.id} variant="overlay" />
                </Box>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: "center", flexWrap: "wrap", gap: 0.5, pr: 5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(p.publishedAt).format("D MMM YYYY")}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {p.platforms.map((pl) => (
                    <Chip key={pl} size="small" variant="outlined" label={pl} />
                  ))}
                </Stack>
                {p.mediaUrl && p.mediaType === "image" && (
                  <Box
                    component="img"
                    src={p.mediaUrl}
                    alt=""
                    loading="lazy"
                    sx={{
                      width: "100%",
                      maxHeight: 360,
                      objectFit: "cover",
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: "action.hover",
                    }}
                  />
                )}
                {p.mediaUrl && p.mediaType === "video" && (
                  <Box
                    component="video"
                    src={p.mediaUrl}
                    controls
                    playsInline
                    sx={{
                      width: "100%",
                      maxHeight: 360,
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: "common.black",
                    }}
                  />
                )}
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {p.text}
                </Typography>
              </Box>
            ))}
          </Stack>
          {filter === "all" && posts.length > 6 && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <RouterLink to="/posts" style={{ color: "inherit" }}>
                <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700 }}>
                  See all Facebook posts →
                </Typography>
              </RouterLink>
            </Box>
          )}
        </Box>
      )}

      {!articlesLoading && !postsLoading && articles.length === 0 && posts.length === 0 && (
        <Alert severity="info">No articles or posts published yet.</Alert>
      )}

      <VideoLightbox {...lightboxProps} />
    </>
  );
}
